import { ReactNode, useState, useRef, useEffect } from 'react'
import { RefreshCw } from 'lucide-react'

interface PullToRefreshProps {
  children: ReactNode
  onRefresh: () => Promise<void>
  threshold?: number
  className?: string
  disabled?: boolean
}

export function PullToRefresh({ 
  children, 
  onRefresh, 
  threshold = 80,
  className = '',
  disabled = false
}: PullToRefreshProps) {
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [pullDistance, setPullDistance] = useState(0)
  const [startY, setStartY] = useState(0)
  const [canPull, setCanPull] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const handleTouchStart = (e: TouchEvent) => {
    if (disabled || isRefreshing) return
    
    const container = containerRef.current
    if (!container) return

    // Only allow pull-to-refresh when at the top of the page
    if (container.scrollTop === 0) {
      setStartY(e.touches[0].clientY)
      setCanPull(true)
    }
  }

  const handleTouchMove = (e: TouchEvent) => {
    if (disabled || isRefreshing || !canPull) return

    const currentY = e.touches[0].clientY
    const distance = currentY - startY

    if (distance > 0) {
      // Prevent default scrolling when pulling down
      e.preventDefault()
      
      // Apply resistance to the pull distance
      const resistance = 0.5
      const adjustedDistance = distance * resistance
      setPullDistance(Math.min(adjustedDistance, threshold * 1.5))
    } else {
      setCanPull(false)
      setPullDistance(0)
    }
  }

  const handleTouchEnd = async () => {
    if (disabled || isRefreshing || !canPull) return

    if (pullDistance >= threshold) {
      setIsRefreshing(true)
      try {
        await onRefresh()
      } catch (error) {
        console.error('Refresh failed:', error)
      } finally {
        setIsRefreshing(false)
      }
    }

    setPullDistance(0)
    setCanPull(false)
    setStartY(0)
  }

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    container.addEventListener('touchstart', handleTouchStart, { passive: false })
    container.addEventListener('touchmove', handleTouchMove, { passive: false })
    container.addEventListener('touchend', handleTouchEnd, { passive: true })

    return () => {
      container.removeEventListener('touchstart', handleTouchStart)
      container.removeEventListener('touchmove', handleTouchMove)
      container.removeEventListener('touchend', handleTouchEnd)
    }
  }, [canPull, pullDistance, startY, threshold, disabled, isRefreshing])

  const refreshProgress = Math.min(pullDistance / threshold, 1)
  const showRefreshIndicator = pullDistance > 0 || isRefreshing

  return (
    <div 
      ref={containerRef}
      className={`relative overflow-auto ${className}`}
      style={{ 
        transform: `translateY(${pullDistance}px)`,
        transition: isRefreshing || pullDistance === 0 ? 'transform 0.3s ease-out' : 'none'
      }}
    >
      {/* Refresh Indicator */}
      {showRefreshIndicator && (
        <div 
          className="absolute top-0 left-0 right-0 flex items-center justify-center bg-primary-50 dark:bg-primary-900/20 border-b border-primary-200 dark:border-primary-800 z-10"
          style={{ 
            height: `${Math.max(pullDistance, isRefreshing ? 60 : 0)}px`,
            transform: `translateY(-${Math.max(pullDistance, isRefreshing ? 60 : 0)}px)`
          }}
        >
          <div className="flex items-center space-x-2 text-primary-600 dark:text-primary-400">
            <RefreshCw 
              className={`w-5 h-5 ${isRefreshing ? 'animate-spin' : ''}`}
              style={{ 
                transform: `rotate(${refreshProgress * 360}deg)`,
                transition: isRefreshing ? 'none' : 'transform 0.1s ease-out'
              }}
            />
            <span className="text-sm font-medium">
              {isRefreshing 
                ? 'Refreshing...' 
                : pullDistance >= threshold 
                  ? 'Release to refresh' 
                  : 'Pull to refresh'
              }
            </span>
          </div>
        </div>
      )}

      {/* Content */}
      <div style={{ paddingTop: isRefreshing ? '60px' : '0px' }}>
        {children}
      </div>
    </div>
  )
}

// Simplified version for non-touch devices
export function SimpleRefreshButton({ 
  onRefresh, 
  isRefreshing = false,
  className = '' 
}: {
  onRefresh: () => Promise<void>
  isRefreshing?: boolean
  className?: string
}) {
  const [loading, setLoading] = useState(false)

  const handleRefresh = async () => {
    if (loading || isRefreshing) return
    
    setLoading(true)
    try {
      await onRefresh()
    } catch (error) {
      console.error('Refresh failed:', error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <button
      onClick={handleRefresh}
      disabled={loading || isRefreshing}
      className={`inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
    >
      <RefreshCw className={`w-4 h-4 mr-2 ${(loading || isRefreshing) ? 'animate-spin' : ''}`} />
      {loading || isRefreshing ? 'Refreshing...' : 'Refresh'}
    </button>
  )
}